import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPOSShift } from './pos-masterlist.slice';

export interface StationEntry {
  shiftID: string;
  selectedRole: string;
  selectedShift: IPOSShift;
  selectedPrinter?: string;
}
export interface Station {
  tenantID: string;
  propertyID: string;
  stationID: string;
  stationName: string;
  ptuNo: string;
  ptuDateStart: string;
  ptuDateExpiry: string;
  ptuMin: string;
  machineSerialNo: string;
  subModuleCode: string;
}

interface RoleState {
  stations: Record<string, StationEntry>;
  selectedStation?: Station;
  initialRole?: string;
  initialPrinter?: string;
  initialShift?: IPOSShift;
  hasSelectRole?: boolean;
  hasInitialShift?: {
    stationID: string;
    station: StationEntry;
  };
  hasPrinter?: boolean;
}

const initialState: RoleState = {
  stations: {},
  selectedStation: undefined,
  initialRole: undefined,
  initialPrinter: undefined,
  initialShift: undefined,
  hasSelectRole: undefined,
  hasInitialShift: undefined,
  hasPrinter: undefined,
};

export const POSStationsSlice = createSlice({
  name: 'posStations',
  initialState,
  reducers: {
    // Add a new entry with both role and shift
    addPOSStationEntry: (
      state,
      action: PayloadAction<{
        stationID: string;
        selectedRole: string;
        selectedShift: IPOSShift;
        selectedPrinter: string;
        shiftID: string;
      }>,
    ) => {
      const {
        stationID,
        selectedRole,
        selectedShift,
        shiftID,
        selectedPrinter,
      } = action.payload;
      if (!stationID) return;

      state.stations[stationID] = {
        shiftID,
        selectedRole,
        selectedShift,
        selectedPrinter,
      };
    },

    updatePOSStationEntry: (state) => {
      if (!state.hasInitialShift) return;

      const { stationID, station } = state.hasInitialShift;
      const existing = state.stations[stationID];

      if (!existing) {
        state.stations[stationID] = {
          shiftID: station.shiftID,
          selectedRole: station.selectedRole,
          selectedShift: station.selectedShift,
          selectedPrinter: station.selectedPrinter,
        };
        return;
      }

      state.stations[stationID] = {
        ...existing,
        ...station,
      };
    },

    addPOSInitialShift: (
      state,
      action: PayloadAction<{
        stationID: string;
        station: StationEntry;
      }>,
    ) => {
      state.hasInitialShift = action.payload;
    },

    clearPOSInitialShift: (state) => {
      state.hasInitialShift = undefined;
    },

    removePOSStationEntry: (state, action: PayloadAction<string>) => {
      const stationID = action.payload;
      if (!stationID) return;

      // Only delete if the station exists
      if (state.stations[stationID]) {
        delete state.stations[stationID];
      }
    },

    // Clear all entries for a station
    clearPOSStation: (state, action: PayloadAction<boolean | undefined>) => {
      if (action.payload) {
        return initialState;
      }

      return {
        ...initialState,
        initialRole: state.initialRole,
        hasSelectRole: state.hasSelectRole,
      };
    },

    setPOSSelectedStationSlice: (
      state,
      action: PayloadAction<Station | undefined>,
    ) => {
      state.selectedStation = action.payload;
    },

    setPOSInitialSelectedRoleSlice: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.initialRole = action.payload;
    },

    setPOSInitialSelectedShiftSlice: (
      state,
      action: PayloadAction<IPOSShift | undefined>,
    ) => {
      state.initialShift = action.payload;
    },

    setPOSInitialSelectedPrinterSlice: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.initialPrinter = action.payload;
    },

    setPOSHasSelectRoleSlice: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.hasSelectRole = action.payload;
    },
    setPOSHasPrinterSlice: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.hasPrinter = action.payload;
    },
  },
});

export const {
  addPOSStationEntry,
  clearPOSStation,
  setPOSSelectedStationSlice,
  setPOSInitialSelectedRoleSlice,
  setPOSInitialSelectedShiftSlice,
  updatePOSStationEntry,
  removePOSStationEntry,
  setPOSInitialSelectedPrinterSlice,
  setPOSHasSelectRoleSlice,
  addPOSInitialShift,
  clearPOSInitialShift,
  setPOSHasPrinterSlice,
} = POSStationsSlice.actions;

export default POSStationsSlice.reducer;
